import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthModalService } from 'src/app/Services/auth-modal.service';
import { TokenService } from 'src/app/Services/token.service';
import { ThemePreferenceService, ThemeMode } from 'src/app/Services/theme-preference.service';
import { UserserviceService } from 'src/app/Services/userservice.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-legal-center-layout',
  templateUrl: './legal-center-layout.component.html',
  styleUrls: ['./legal-center-layout.component.scss'],
  host: {
    'class': 'legal-page'
  }
})
export class LegalCenterLayoutComponent implements OnInit, OnDestroy {
  selectedThemeMode: "light" | "dark" = "light";
  private themeSubscription?: Subscription;
  isMobileView = false;
  private resizeListener?: () => void;
  @Input() pageTitle: string = '';
  @Input() lastUpdated: string = '';
  @Input() version: string = '';
  @Input() sections: Array<{id: string, title: string}> = [];
  @Input() currentPage: string = '';

  legalPages = [
    { route: '/termsandconditions', title: 'Terms of Service', icon: 'description', active: false },
    { route: '/privacypolicy', title: 'Privacy Policy', icon: 'verified_user', active: false },
    { route: '/community-guidelines', title: 'Community Guidelines', icon: 'groups', active: false },
    { route: '/cookie-policy', title: 'Cookie Policy', icon: 'cookie', active: false },
    { route: '/law-enforcement', title: 'Law Enforcement', icon: 'gavel', active: false },
    { route: '/thirdparty', title: 'Third-Party Libraries', icon: 'library_books', active: false }
  ];

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private authModalService: AuthModalService,
    private tokenService: TokenService,
    private themePreferenceService: ThemePreferenceService,
    private userserviceService: UserserviceService
  ) {}

  ngOnInit() {
    // Subscribe first so the emission from initializeFromStorage() is caught synchronously
    this.themeSubscription = this.themePreferenceService.themeChanges$.subscribe((theme) => {
      this.applyThemeMode(theme);
    });
    this.initializeThemeMode();
    // Set active page based on current route
    const currentRoute = this.router.url;
    this.legalPages.forEach(page => {
      // Check if current route matches exactly or starts with the page route
      page.active = currentRoute === page.route || currentRoute.startsWith(page.route + '/');
    });
    this.checkMobileView();
    if (typeof window !== 'undefined') {
      this.resizeListener = () => this.checkMobileView();
      window.addEventListener('resize', this.resizeListener);
    }
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  checkMobileView(): void {
    if (typeof window !== 'undefined') {
      this.isMobileView = window.innerWidth <= 768;
    }
  }

  get isDarkThemeActive(): boolean {
    return this.selectedThemeMode === "dark";
  }

  toggleThemeMode(): void {
    const nextTheme = this.themePreferenceService.toggleTheme();
    this.persistThemeMode(nextTheme);
  }

  private persistThemeMode(theme: "light" | "dark"): void {
    const userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    if (userData?.emailAddress) {
      this.userserviceService
        .UpdateThemeSettingsForCustomerProfileDetails({
          emailAddress: userData.emailAddress,
          selectedTheme: theme,
        })
        .subscribe({
          next: () => {
            userData.selectedTheme = theme;
            localStorage.setItem("LoggedInUser", JSON.stringify(userData));
          },
          error: () => {},
        });
    }
  }

  private initializeThemeMode(): void {
    // Re-reads localStorage so pages that bypass ThemePreferenceService (e.g. how-it-works)
    // are still accounted for. The return value lets us set selectedThemeMode immediately
    // without waiting for the BehaviorSubject replay.
    const theme = this.themePreferenceService.initializeFromStorage();
    this.selectedThemeMode = theme;
  }

  private applyThemeMode(theme: ThemeMode): void {
    this.selectedThemeMode = theme;
  }

  navigateToPage(route: string) {
    this.router.navigate([route]);
  }

  scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  downloadPDF() {
    // Implement PDF download functionality
    console.log('Download PDF for', this.pageTitle);
  }

  isUserLoggedIn(): boolean {
    const refreshToken = this.tokenService.getRefreshToken();
    const userData = localStorage.getItem("LoggedInUser");
    return !!(refreshToken && userData && userData !== 'null');
  }

  navigateHome() {
    if (this.isUserLoggedIn()) {
      // If logged in, navigate to home page
      this.router.navigate(['/']);
    } else {
      // If not logged in, show sign-up modal (Explore action)
      this.authModalService.openModal('registration');
    }
  }

  navigateSignIn() {
    if (this.isUserLoggedIn()) {
      // If logged in, navigate to home page
      this.router.navigate(['/']);
    } else {
      // If not logged in, show sign-up modal (like how-it-works)
      this.authModalService.openModal('registration');
    }
  }

  navigateToBuild() {
    if (this.isUserLoggedIn()) {
      // If logged in, navigate to create-flow page
      this.router.navigate(['/create-guide']);
    } else {
      // If not logged in, show sign-up modal
      this.authModalService.openModal('registration');
    }
  }

  navigateToExplore() {
    if (this.isUserLoggedIn()) {
      // If logged in, navigate to home page
      this.router.navigate(['/']);
    } else {
      // If not logged in, show sign-up modal
      this.authModalService.openModal('registration');
    }
  }

  /**
   * Some routes opened from legal pages have fixed headers and can render with
   * top lines clipped. Re-apply top scroll after navigation settles.
   */
  normalizeScrollAfterNavigation(): void {
    setTimeout(() => window.scrollTo(0, 0), 0);
    setTimeout(() => window.scrollTo(0, 0), 80);
  }

  toggleSidebar(): void {
    // Access the app component's sidebar through DOM or service
    // Since sidebar is controlled by app component, we'll trigger it via custom event
    const event = new CustomEvent('toggleSidebar');
    window.dispatchEvent(event);
    
    // Also try to directly access the app component if available
    const appComponent = (window as any).appComponent;
    if (appComponent && appComponent.toggleSidebar) {
      appComponent.toggleSidebar();
    }
  }
}

